import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useComponentSize } from 'react-use-size';
import { Button } from 'antd';
import { MdModeEdit } from 'react-icons/md';
import { IoIosDownload } from 'react-icons/io';
import VideoLoadingIndicator from './VideoLoadingIndicator';

interface UserChimeIDs {
  clinician: string;
  patient: string;
}

const possiblePreviewPositions = [
  { top: 0, left: 0 },
  { top: 0, right: 0 },
  { bottom: 0, left: 0 },
  { bottom: 0, right: 0 }
];

const PreviewContainer = ({
  changePreviewPosition,
  height,
  position
}: {
  changePreviewPosition: () => void;
  height: number;
  position: 0 | 1 | 2 | 3;
}) => {
  const { t } = useTranslation('common');
  return (
    <Button
      data-testid="preview-position-button"
      onClick={() => changePreviewPosition()}
      style={{
        width: '35%',
        height,
        minHeight: '35%',
        maxHeight: '50%',
        display: 'flex',
        border: '1px solid lightgrey',
        backgroundColor: '#555555',
        position: 'absolute',
        borderRadius: 8,
        margin: 8,
        cursor: 'pointer',
        flexDirection: 'column',
        justifyContent: 'center',
        textAlign: 'center',
        ...possiblePreviewPositions[position]
      }}
    >
      <div style={{ fontSize: '1rem', color: '#ECECEC', width: '100%' }}>
        <p style={{ textWrap: 'wrap' }}>
          <IoIosDownload /> {t('Click here to change')}
        </p>
      </div>
    </Button>
  );
};

const ParticipantVideoPositions = ({
  isClinician,
  clinicianIsScreensharing,
  patientIsScreensharing,
  initialisedVideoStreams,
  userChimeIDs
}: {
  isClinician: boolean;
  clinicianIsScreensharing: boolean;
  patientIsScreensharing: boolean;
  initialisedVideoStreams: boolean;
  userChimeIDs?: UserChimeIDs;
}) => {
  const { t } = useTranslation('common');
  const { ref, height } = useComponentSize();
  const [isPreview, setIsPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState(possiblePreviewPositions[3]);
  const localIsScreensharing = isClinician ? clinicianIsScreensharing : patientIsScreensharing;
  const remoteIsScreensharing = isClinician ? patientIsScreensharing : clinicianIsScreensharing;
  const localUserID = isClinician ? userChimeIDs?.clinician : userChimeIDs?.patient;
  const remoteUserID = isClinician ? userChimeIDs?.patient : userChimeIDs?.clinician;
  const changePreviewPosition = (position: 0 | 1 | 2 | 3) => {
    setPreviewPosition(possiblePreviewPositions[position]);
    setIsPreview(false);
  };
  return (
    <div style={{ position: 'absolute', width: '100%', height: '100%' }}>
      <VideoLoadingIndicator initialisedVideoStreams={initialisedVideoStreams} />
      <video
        style={{
          width: '100%',
          height: '100%',
          top: '0',
          left: '0',
          display: remoteIsScreensharing ? 'inline' : 'none',
          position: 'absolute',
          border: '1px solid lightgrey',
          borderRadius: 8
        }}
        id={`${remoteUserID}#content`}
      />
      <video
        style={{
          width: '100%',
          height: '100%',
          top: '0',
          left: '0',
          display: remoteIsScreensharing ? 'none' : 'inline',
          position: 'absolute',
          border: '1px solid lightgrey',
          borderRadius: 8,
          objectFit: 'cover'
        }}
        id="remote_video"
      />
      <div
        style={{
          width: '35%',
          display: 'inline',
          border: '1px solid lightgrey',
          position: 'absolute',
          zIndex: 1000,
          borderRadius: 8,
          margin: 8,
          ...previewPosition
        }}
      >
        <Button
          icon={<MdModeEdit />}
          style={{ position: 'absolute', top: 2, right: 2, zIndex: 1500 }}
          type="primary"
          shape="circle"
          data-testid="edit"
          aria-label={t('Edit video position')}
          onClick={() => setIsPreview(true)}
        />
        <video
          ref={ref}
          style={{
            width: '100%',
            display: 'inline',
            borderRadius: 8
          }}
          id="local_video"
        />
        <video
          style={{
            width: '100%',
            display: localIsScreensharing ? 'inline' : 'none',
            borderRadius: 8
          }}
          id={`${localUserID}#content`}
        />
      </div>
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 2000,
          borderRadius: 8,
          backdropFilter: 'blur(1px)',
          display: isPreview ? 'inline' : 'none'
        }}
      >
        <PreviewContainer changePreviewPosition={() => changePreviewPosition(0)} height={height} position={0} />
        <PreviewContainer changePreviewPosition={() => changePreviewPosition(1)} height={height} position={1} />
        <PreviewContainer changePreviewPosition={() => changePreviewPosition(2)} height={height} position={2} />
        <PreviewContainer changePreviewPosition={() => changePreviewPosition(3)} height={height} position={3} />
      </div>
    </div>
  );
};

export default ParticipantVideoPositions;
